/**
 * Guide slug map — maps guideKey → URL slug per language.
 *
 * Used by:
 *  - GuideDetail page to resolve slug → guideKey (for any language)
 *  - LocalizedLink / buildLocalizedPath to generate correct URLs
 *  - _worker.js (mirrored as GUIDE_SLUG_MAPS) for server-side redirects
 *
 * KEEP IN SYNC with _worker.js GUIDE_SLUG_MAPS object.
 *
 * Language strategy:
 *  - EN/FR/DE/IT: Latin-script localized slugs
 *  - RU: Latin transliteration (e.g. "chlenstvo") so URLs survive copy-paste
 *  - ZH/JA: English slugs (pinyin/romaji aren't meaningful to native speakers;
 *    major international sites like Booking/Airbnb/Agoda do the same)
 */

export const guideSlugMap: Record<string, Partial<Record<string, string>>> = {
  visaRequirements: {
    // EN-only until the translations land. Adding a language here is what makes
    // the worker emit its hreflang + sitemap entry, so a slug must never be
    // listed before guides.articles.visaRequirements exists in that language
    // file — otherwise we advertise six URLs of English content.
    // Step 2 slugs, ready to uncomment with the translations:
    //   fr: "conditions-visa", de: "visum-voraussetzungen",
    //   it: "requisiti-visto", ru: "trebovaniya-k-vize",
    //   zh: "visa-requirements", ja: "visa-requirements",
    en: "visa-requirements",
  },
  stayExtension: {
    en: "stay-extension",
    fr: "extension-sejour-de-1-an",
    de: "aufenthaltserlaengung",
    it: "estensione-soggiorno-1-anno",
    ru: "prodlenie-prebyvaniya",
    zh: "stay-extension",
    ja: "stay-extension",
  },
  taxes: {
    en: "tax-guide",
    fr: "guide-fiscal",
    de: "steuerguide",
    it: "guida-fiscale",
    ru: "nalogovoe-rukovodstvo",
    zh: "tax-guide",
    ja: "tax-guide",
  },
  bankAccount: {
    en: "bank-opening",
    fr: "ouverture-de-compte-bancaire",
    de: "bank-opening",
    it: "apertura-conto-bancario",
    ru: "otkrytie-scheta",
    zh: "bank-opening",
    ja: "bank-opening",
  },
  driving: {
    en: "driving-license",
    fr: "permis-de-conduire",
    de: "fuehrerschein",
    it: "patente-di-guida",
    ru: "voditelskie-prava",
    zh: "driving-license",
    ja: "driving-license",
  },
  adminDeclarations: {
    en: "administrative-declarations",
    fr: "declarations-administratives",
    de: "administrative-erklaerungen",
    it: "dichiarazioni-amministrative",
    ru: "administrativnye-deklaratsii",
    zh: "administrative-declarations",
    ja: "administrative-declarations",
  },
  tm30: {
    en: "tm30",
    fr: "tm30",
    de: "tm30",
    it: "tm30",
    ru: "tm30",
    zh: "tm30",
    ja: "tm30",
  },
  annualExtension: {
    en: "annual-extension",
    fr: "extension-annuelle",
    de: "jaehrliche-verlaengerung",
    it: "estensione-annuale",
    ru: "ezhegodnoe-prodlenie",
    zh: "annual-extension",
    ja: "annual-extension",
  },
  ninetyDays: {
    en: "90-day-reporting",
    fr: "notification-des-90-jours",
    de: "90-tage-meldung",
    it: "rapporto-90-giorni",
    ru: "90-dnevnyy-otchet",
    zh: "90-day-reporting",
    ja: "90-day-reporting",
  },
  overstays: {
    en: "overstay-rules",
    fr: "depassement-de-sejour",
    de: "ueberzug-regeln",
    it: "regole-overstay",
    ru: "pravila-overstaya",
    zh: "overstay-rules",
    ja: "overstay-rules",
  },
  residenceCert: {
    en: "residence-certificate",
    fr: "certificat-de-residence",
    de: "aufenthaltsbescheinigung",
    it: "certificato-residenza",
    ru: "spravka-o-prozhivanii",
    zh: "residence-certificate",
    ja: "residence-certificate",
  },
  tdac: {
    en: "tdac",
    fr: "tdac",
    de: "tdac",
    it: "tdac",
    ru: "tdac",
    zh: "tdac",
    ja: "tdac",
  },
  visaAffixation: {
    en: "visa-affixation",
    fr: "apposition-du-visa",
    de: "visa-affixation",
    it: "apposizione-visto",
    ru: "prostavlenie-vizy",
    zh: "visa-affixation",
    ja: "visa-affixation",
  },
};

/**
 * Reverse map: lang → slug → guideKey
 * Built automatically from guideSlugMap above.
 */
export const slugToGuideKey: Record<string, Record<string, string>> = {};

for (const [key, langs] of Object.entries(guideSlugMap)) {
  for (const [lang, slug] of Object.entries(langs)) {
    if (!slug) continue;
    if (!slugToGuideKey[lang]) slugToGuideKey[lang] = {};
    slugToGuideKey[lang][slug] = key;
  }
}

/**
 * Resolve a slug (in any language) to a guide key.
 * Falls back to checking EN slugs if language-specific lookup fails.
 */
export function resolveGuideSlug(slug: string, lang: string): string | null {
  // Try current language first
  const langMap = slugToGuideKey[lang];
  if (langMap?.[slug]) return langMap[slug];

  // Fallback: try English slugs (useful for cross-language links)
  const enMap = slugToGuideKey["en"];
  if (enMap?.[slug]) return enMap[slug];

  // Fallback: try all languages (slugs may be shared, e.g. tm30)
  for (const map of Object.values(slugToGuideKey)) {
    if (map[slug]) return map[slug];
  }

  return null;
}

/**
 * Get the localized slug for a guide key in a given language.
 * Falls back to English slug if no translation exists.
 */
export function getGuideSlug(guideKey: string, lang: string): string {
  return guideSlugMap[guideKey]?.[lang] ?? guideSlugMap[guideKey]?.["en"] ?? guideKey;
}

/**
 * Guides that exist in only some languages. A guide key absent from this map is
 * available everywhere.
 *
 * KEEP IN SYNC with the slug entries above and with EN_ONLY_GUIDE_SLUGS in
 * scripts/prerender.mjs. A guide must never be linked in a language whose i18n
 * file has no `guides.articles.<key>` block: i18next would silently fall back
 * to English and serve English copy under a localized URL.
 */
export const guideLanguages: Record<string, readonly string[]> = {
  visaRequirements: ["en"],
};

export function isGuideAvailable(guideKey: string, lang: string | undefined): boolean {
  const langs = guideLanguages[guideKey];
  if (!langs) return true;
  return langs.includes((lang || "en").split("-")[0].toLowerCase());
}
